import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Zap, Eye, EyeOff, LogIn, UserPlus, Globe } from 'lucide-react';
import { useAppStore } from '../stores/appStore';
import { useTranslation } from '../i18n/useTranslation';
import { mockUser, mockSites } from '../services/mockData';
import { authService, isFirebaseConfigured } from '../services/firebase';
import { clsx } from 'clsx';

export default function LoginPage() {
  const navigate = useNavigate();
  const { setUser, setMySites, showToast } = useAppStore();
  const { t, lang, setLang } = useTranslation();

  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);

  const handleFirebaseError = (err: unknown) => {
    const code = (err as { code?: string })?.code || '';
    if (code.includes('user-not-found') || code.includes('wrong-password') || code.includes('invalid-credential')) {
      showToast(t('login.err_invalid'), 'error');
    } else if (code.includes('email-already-in-use')) {
      showToast(t('login.err_email_used'), 'error');
    } else if (code.includes('weak-password')) {
      showToast(t('login.err_weak'), 'error');
    } else {
      showToast((err as Error).message || t('common.error'), 'error');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      showToast(t('login.err_fields'), 'error');
      return;
    }

    setIsLoading(true);

    if (!isFirebaseConfigured) {
      // Demo mode
      await new Promise((r) => setTimeout(r, 1200));
      setUser({ ...mockUser, email, displayName: name || email.split('@')[0] });
      setMySites(mockSites);
      setIsLoading(false);
      showToast(`${t('login.welcome')}, ${name || email.split('@')[0]}! 👋`, 'success');
      navigate('/dashboard');
      return;
    }

    try {
      if (mode === 'login') {
        await authService.signInWithEmail(email, password);
      } else {
        await authService.registerWithEmail(email, password, name);
      }
      navigate('/dashboard', { replace: true });
    } catch (err) {
      handleFirebaseError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogle = async () => {
    if (!isFirebaseConfigured) {
      showToast(t('login.demo'), 'info');
      return;
    }
    setIsGoogleLoading(true);
    try {
      await authService.signInWithGoogle();
      navigate('/dashboard', { replace: true });
    } catch (err) {
      handleFirebaseError(err);
    } finally {
      setIsGoogleLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-surface-900 via-surface-800 to-telegram/20 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        {/* Language Toggle */}
        <div className="flex justify-end mb-4">
          <button
            onClick={() => setLang(lang === 'ms' ? 'en' : 'ms')}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/10 backdrop-blur-sm border border-white/20 text-white text-sm font-medium hover:bg-white/20 transition-all"
          >
            <Globe size={14} />
            {lang === 'ms' ? '🇲🇾 BM' : '🇬🇧 EN'}
          </button>
        </div>

        {/* Logo */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 telegram-gradient rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-glow">
            <Zap size={32} className="text-white" />
          </div>
          <h1 className="text-3xl font-extrabold text-white">
            Tele<span className="text-telegram">Web</span>
          </h1>
          <p className="text-surface-400 mt-2">{t('login.tagline')}</p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
          {/* Tab */}
          <div className="flex border-b border-surface-100">
            {(['login', 'register'] as const).map((m) => (
              <button
                key={m}
                onClick={() => setMode(m)}
                className={clsx(
                  'flex-1 py-4 text-sm font-semibold transition-all border-b-2',
                  mode === m
                    ? 'border-telegram text-telegram bg-telegram/5'
                    : 'border-transparent text-surface-500 hover:text-surface-700'
                )}
              >
                {m === 'login' ? t('login.tab_login') : t('login.tab_register')}
              </button>
            ))}
          </div>

          <div className="p-8">
            <form onSubmit={handleSubmit} className="space-y-5">
              {mode === 'register' && (
                <div>
                  <label className="block text-sm font-medium text-surface-700 mb-1.5">{t('login.name')}</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={t('login.name_ph')}
                    className="input"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-surface-700 mb-1.5">{t('login.email')}</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="nama@email.com"
                  className="input"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-surface-700 mb-1.5">{t('login.password')}</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="input pr-10"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-surface-400 hover:text-surface-600"
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="btn-primary btn-lg w-full"
              >
                {isLoading ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    {mode === 'login' ? t('login.logging_in') : t('login.registering')}
                  </span>
                ) : (
                  <>
                    {mode === 'login' ? <LogIn size={18} /> : <UserPlus size={18} />}
                    {mode === 'login' ? t('login.submit_login') : t('login.submit_register')}
                  </>
                )}
              </button>
            </form>

            <div className="flex items-center gap-3 my-6">
              <div className="flex-1 h-px bg-surface-200" />
              <span className="text-surface-400 text-sm">{t('login.or')}</span>
              <div className="flex-1 h-px bg-surface-200" />
            </div>

            <div className="space-y-3">
              <button
                onClick={handleGoogle}
                disabled={isGoogleLoading}
                className="btn-outline btn-md w-full"
              >
                {isGoogleLoading ? (
                  <span className="w-4 h-4 border-2 border-surface-300 border-t-surface-600 rounded-full animate-spin" />
                ) : (
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                )}
                {t('login.google')}
              </button>
            </div>
          </div>
        </div>

        {/* Features */}
        <div className="mt-6 grid grid-cols-3 gap-3">
          {[
            { icon: '⚡', text: 'Deploy < 2 min' },
            { icon: '🔒', text: 'Auto Security' },
            { icon: '🆓', text: t('home.stat_free') },
          ].map((f) => (
            <div key={f.text} className="bg-white/10 backdrop-blur-sm rounded-xl p-3 text-center">
              <div className="text-2xl mb-1">{f.icon}</div>
              <div className="text-white/80 text-xs font-medium">{f.text}</div>
            </div>
          ))}
        </div>

        {/* Firebase status */}
        <div className="mt-4 bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center">
          <p className="text-white/60 text-xs">
            {isFirebaseConfigured
              ? '🔥 Firebase connected — login sebenar aktif'
              : t('login.demo')}
          </p>
        </div>
      </motion.div>
    </div>
  );
}
